import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  ViewEncapsulation,
} from '@angular/core';
import {
  FUNDRAISING_COMPARISON_DEVELOPMENT_MODE,
  getAvailablePricingConfigurations,
  getDefaultOptionSelections,
  getPlatformCategories,
} from '../../data/platform-pricing.config';
import type {
  FundraiserScenario,
  FundraisingComparisonState,
  PlatformComparisonResult,
  PlatformId,
  PlatformOptionChange,
} from '../../models/fundraising-comparison.models';
import { ComparisonAnalyticsService } from '../../services/comparison-analytics.service';
import { FundraisingCalculationCoordinator } from '../../services/fundraising-calculation-coordinator';
import { UrlScenarioStateService } from '../../services/url-scenario-state.service';
import { FundraiserScenarioComponent } from '../fundraiser-scenario/fundraiser-scenario.component';
import { FundraisingExplorerResultsComponent } from '../fundraising-explorer-results/fundraising-explorer-results.component';

@Component({
  standalone: true,
  selector: 'fundraising-comparison',
  imports: [
    CommonModule,
    FundraiserScenarioComponent,
    FundraisingExplorerResultsComponent,
  ],
  templateUrl: './fundraising-comparison.component.html',
  styleUrls: ['./fundraising-comparison.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FundraisingComparisonComponent implements OnInit, OnDestroy {
  readonly developmentMode = FUNDRAISING_COMPARISON_DEVELOPMENT_MODE;
  readonly availablePlatforms = getAvailablePricingConfigurations();
  readonly categories = getPlatformCategories();
  readonly enabledPlatformIds = this.availablePlatforms.map(
    (platform) => platform.id,
  );

  state!: FundraisingComparisonState;
  scenario!: FundraiserScenario;
  results: PlatformComparisonResult[] = [];
  liveAnnouncement = '';
  copyStatus = '';

  private readonly calculationCoordinator =
    new FundraisingCalculationCoordinator();
  private urlUpdateTimeout?: ReturnType<typeof setTimeout>;
  private announcementTimeout?: ReturnType<typeof setTimeout>;
  private readonly popStateListener = () => this.restoreFromUrl();

  constructor(
    private readonly urlState: UrlScenarioStateService,
    private readonly analytics: ComparisonAnalyticsService,
    private readonly cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.state = this.urlState.read();
    this.recalculate(false);
    window.addEventListener('popstate', this.popStateListener);
    this.analytics.track('fundraising_comparison_loaded', {
      pricing_mode: this.developmentMode ? 'development' : 'verified',
    });
  }

  ngOnDestroy(): void {
    window.removeEventListener('popstate', this.popStateListener);
    if (this.urlUpdateTimeout) {
      clearTimeout(this.urlUpdateTimeout);
    }
    if (this.announcementTimeout) {
      clearTimeout(this.announcementTimeout);
    }
  }

  onIntendedGiftTotalCentsChange(intendedGiftTotalCents: number): void {
    this.state = {
      ...this.state,
      intendedGiftTotalCents,
      donationCountOverride:
        intendedGiftTotalCents > 0 && this.scenario.transactions.length > 0
          ? this.scenario.transactions.length
          : null,
    };
    this.recalculate();
    this.analytics.track('fundraising_scenario_changed', {
      amount_bucket: this.analytics.amountBucket(intendedGiftTotalCents),
      donation_count_bucket: this.analytics.donationCountBucket(
        this.scenario.transactions.length,
      ),
      profile_id: this.state.profileId,
      donation_count_source: this.scenario.donationCountSource ?? 'profile',
    });
  }

  onDonationCountOverrideChange(donationCountOverride: number | null): void {
    this.state = { ...this.state, donationCountOverride };
    this.recalculate();
    this.analytics.track('fundraising_scenario_changed', {
      amount_bucket: this.analytics.amountBucket(
        this.state.intendedGiftTotalCents,
      ),
      donation_count_bucket: this.analytics.donationCountBucket(
        this.scenario.transactions.length,
      ),
      profile_id: this.state.profileId,
      donation_count_source: this.scenario.donationCountSource ?? 'profile',
    });
  }

  onOptionChange(change: PlatformOptionChange): void {
    this.state = {
      ...this.state,
      optionSelections: {
        ...this.state.optionSelections,
        [change.platformId]: {
          ...this.state.optionSelections[change.platformId],
          [change.optionGroupId]: change.choiceId,
        },
      },
    };
    this.recalculate();
    this.analytics.track('fundraising_platform_option_changed', {
      platform: change.platformId,
      option_group: change.optionGroupId,
      choice: change.choiceId,
    });
    const result = this.resultFor(change.platformId);
    this.analytics.track('platform_quick_configuration_changed', {
      platformId: change.platformId,
      configurationId: `${change.optionGroupId}:${change.choiceId}`,
      pricingStatus: result?.evidence.status ?? 'unknown',
    });
  }

  onQuickViewOpened(platformId: PlatformId): void {
    const result = this.resultFor(platformId);
    this.analytics.track('platform_quick_view_opened', {
      platformId,
      configurationId: this.configurationId(result),
      pricingStatus: result?.evidence.status ?? 'unknown',
    });
  }

  onQuickViewClosed(platformId: PlatformId): void {
    const result = this.resultFor(platformId);
    this.analytics.track('platform_quick_view_closed', {
      platformId,
      configurationId: this.configurationId(result),
      pricingStatus: result?.evidence.status ?? 'unknown',
    });
  }

  onQuickViewFullPageClicked(platformId: PlatformId): void {
    const result = this.resultFor(platformId);
    this.analytics.track('platform_full_page_clicked', {
      platformId,
      configurationId: this.configurationId(result),
      pricingStatus: result?.evidence.status ?? 'unknown',
    });
  }

  onDetailLinkClicked(platformId: PlatformId): void {
    this.analytics.track('fundraising_platform_detail_link_clicked', {
      platform: platformId,
      page: 'overview',
    });
  }

  get hasCustomizedConfigurations(): boolean {
    return this.availablePlatforms.some((platform) =>
      platform.optionGroups.some(
        (group) =>
          (this.state.optionSelections[platform.id]?.[group.id] ??
            group.defaultChoiceId) !== group.defaultChoiceId,
      ),
    );
  }

  resetComparison(): void {
    this.state = {
      ...this.state,
      optionSelections: getDefaultOptionSelections(),
    };
    this.recalculate();
    this.analytics.track('comparison_reset', {
      preservedAmount: true,
      preservedDonationCount: true,
    });
  }

  get detailPageUrls(): Partial<Record<PlatformId, string>> {
    return Object.fromEntries(
      this.availablePlatforms
        .filter((platform) => platform.detailPagePath)
        .map((platform) => [
          platform.id,
          this.urlState.buildAbsoluteUrlForPath(
            this.state,
            platform.detailPagePath!,
          ),
        ]),
    );
  }

  async copyComparisonLink(): Promise<void> {
    try {
      await navigator.clipboard.writeText(
        this.urlState.buildAbsoluteUrl(this.state),
      );
      this.copyStatus = 'Comparison link copied.';
      this.analytics.track('fundraising_comparison_link_copied');
    } catch {
      this.copyStatus =
        'Unable to copy the link. Copy it from your browser address bar.';
    }
    this.cdr.markForCheck();
  }

  private recalculate(updateUrl = true): void {
    const snapshot = this.calculationCoordinator.calculate(
      this.state,
      this.enabledPlatformIds,
    );
    this.state = snapshot.state;
    this.scenario = snapshot.scenario;
    this.results = snapshot.results;

    this.copyStatus = '';

    if (updateUrl) {
      this.scheduleUrlUpdate();
      this.scheduleAnnouncement();
    }
    this.cdr.markForCheck();
  }

  private scheduleUrlUpdate(): void {
    if (this.urlUpdateTimeout) {
      clearTimeout(this.urlUpdateTimeout);
    }
    this.urlUpdateTimeout = setTimeout(() => {
      this.urlState.replace(this.state);
    }, 300);
  }

  private restoreFromUrl(): void {
    this.state = this.urlState.read();
    this.recalculate(false);
  }

  private scheduleAnnouncement(): void {
    if (this.announcementTimeout) {
      clearTimeout(this.announcementTimeout);
    }
    this.announcementTimeout = setTimeout(() => {
      this.liveAnnouncement = `Comparison updated for ${this.formatMoney(
        this.scenario.intendedGiftTotalCents,
      )} across ${this.results.length} platforms.`;
      this.cdr.markForCheck();
    }, 300);
  }

  private formatMoney(amountCents: number): string {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: amountCents % 100 === 0 ? 0 : 2,
    }).format(amountCents / 100);
  }

  private resultFor(
    platformId: PlatformId,
  ): PlatformComparisonResult | undefined {
    return this.results.find((result) => result.platformId === platformId);
  }

  private configurationId(
    result: PlatformComparisonResult | undefined,
  ): string {
    if (!result || result.optionGroups.length === 0) return 'none';
    return result.optionGroups
      .map((group) => `${group.id}:${result.optionSelections[group.id]}`)
      .join('|');
  }
}
